import { Box, Center, Modal } from '@mantine/core'
import { useDisclosure, useViewportSize } from '@mantine/hooks'
import { useEffect, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { FileMetadata } from '@/types/dataset'
import { AnnotationOverlay, TOOLBAR_HEIGHT } from './AnnotationOverlay'
import { LabelModeToolbar } from './AnnotationToolbar/LabelModeToolbar'
import { LoadingMessage } from './LoadingMessage/LoadingMessage'
import { LoadingStatus } from './LoadingMessage/status'
import { FormValues, VideoFpsForm } from './VideoFpsForm/VideoFpsForm'
import { useFinishCvatSession } from './useFinishCvatSession'
import { useStartCvatSession } from './useStartCvatSession'

type AnnotationHandlerProps = {
  annotationTaskId?: number
  file: FileMetadata
  datasetVersionId: string
  isAnnotationDisabled?: boolean
  onCancelLabeling: () => void
  onSaveLabeling: () => void
}

export const AnnotationHandler = ({
  annotationTaskId,
  file,
  datasetVersionId,
  isAnnotationDisabled,
  onCancelLabeling,
  onSaveLabeling
}: AnnotationHandlerProps) => {
  // eslint-disable-next-line react-hooks/purity -- TODO: Date.now() in useRef initial value violates the new react-hooks/purity rule introduced in eslint-plugin-react-hooks v7, fix when revisiting this component
  const timestamp = useRef(Date.now())
  const cvatInitialized = useRef(false)
  const iframe = useRef<HTMLIFrameElement | null>(null)

  const [isFpsModalOpened, { open: openFpsModal, close: closeFpsModal }] =
    useDisclosure(false)

  const [loadingStatus, setLoadingStatus] = useState<LoadingStatus | null>(
    LoadingStatus.prepareFile
  )
  const isLoading = loadingStatus !== null

  const { height: vHeight } = useViewportSize()

  const { iframeUrl, token, cvatTaskId, startCvatSession } =
    useStartCvatSession({
      fileMetadataId: file.id,
      datasetVersionId,
      taskName: `cvat_task_${file.id}_${timestamp.current}`,
      taskId: annotationTaskId,
      onError: onCancelLabeling
    })

  const { finishCvatSession, completed } = useFinishCvatSession({
    taskId: cvatTaskId,
    datasetVersionId,
    onError: onCancelLabeling
  })

  const isVideoFile = file.media_type === 'video'

  useEffect(() => {
    if (!cvatInitialized.current) {
      if (isVideoFile && annotationTaskId === undefined) {
        openFpsModal()
      } else {
        cvatInitialized.current = true
        void startCvatSession(1)
      }
    }
  }, [isVideoFile, openFpsModal, startCvatSession, annotationTaskId])

  useEffect(() => {
    if (completed) {
      onSaveLabeling()
    }
  }, [completed, onSaveLabeling])

  const handleFpsSubmit = ({ labelFps }: FormValues) => {
    closeFpsModal()
    void startCvatSession(labelFps)
  }

  const handleIframeLoad = () => {
    setLoadingStatus(null)

    if (token && iframeUrl && iframe.current?.contentWindow) {
      iframe.current.contentWindow.postMessage(
        { action: 'ADD_TOKEN', value: '"' + token + '"' },
        new URL(iframeUrl).origin
      )
    }
  }

  const handleSaveLabeling = () => {
    if (iframeUrl && iframe.current?.contentWindow) {
      iframe.current.contentWindow.postMessage(
        'FINISH_JOB',
        new URL(iframeUrl).origin
      )
    }

    setLoadingStatus(LoadingStatus.saveChanges)
    finishCvatSession()
  }

  return (
    <>
      <AnnotationOverlay
        toolbar={
          isLoading ? null : (
            <LabelModeToolbar
              fileName={file.name}
              fileType={file.media_type}
              isSaveDisabled={isAnnotationDisabled}
              onCancel={onCancelLabeling}
              onSave={handleSaveLabeling}
            />
          )
        }
        opened
      >
        <Box w="100%" h={vHeight - TOOLBAR_HEIGHT} bg="white">
          <Center h="100%">
            {isLoading && !isFpsModalOpened && (
              <LoadingMessage
                imageUrl={file.thumbnail}
                loadingStatus={loadingStatus}
              />
            )}

            {iframeUrl && (
              <iframe
                ref={iframe}
                src={iframeUrl}
                style={{
                  width: '100%',
                  height: vHeight - TOOLBAR_HEIGHT,
                  border: 'none',
                  backgroundColor: 'transparent',
                  display: isLoading ? 'none' : 'block'
                }}
                onLoad={handleIframeLoad}
              />
            )}
          </Center>
        </Box>
      </AnnotationOverlay>

      <Modal
        title={<FormattedMessage id="annotation.labelFps" />}
        opened={isFpsModalOpened}
        onClose={onCancelLabeling}
      >
        <VideoFpsForm
          originalFps={file.original_video_fps || 1}
          onCancel={onCancelLabeling}
          onSubmit={(values) => void handleFpsSubmit(values)}
        />
      </Modal>
    </>
  )
}
